/** Board tab copy (EN / SK / CS). Plural keys carry every CLDR form used by the three locales. */
export const boardCopy = {
  en: {
    title: 'Board',
    loading: 'Loading the board…',
    error: 'The board couldn’t load.',
    retry: 'Try again',
    loadError: {
      title: 'The board couldn’t load',
      body: 'A little ink got in the way. Try again in a moment.',
      refresh: 'The board couldn’t refresh. Your last update is still here.',
    },
    preview: {
      trigger: 'Test error',
      restore: 'Restore board',
      restoreShort: 'Restore',
      menu: 'Error preview options',
      failure: 'Show error · retry fails',
      success: 'Show error · retry succeeds',
      quotes: 'Preview quote carousel',
    },
    month: {
      eyebrow: 'Your {{month}}',
      pages_one: 'page',
      pages_few: 'pages',
      pages_many: 'pages',
      pages_other: 'pages',
      finished_one: 'book finished',
      finished_few: 'books finished',
      finished_many: 'books finished',
      finished_other: 'books finished',
      weeksOnGoal: 'weeks on goal',
      open: 'Your month',
      share: 'Share your month',
      compact_one: '{{pages}} pages · {{count}} book finished',
      compact_few: '{{pages}} pages · {{count}} books finished',
      compact_many: '{{pages}} pages · {{count}} books finished',
      compact_other: '{{pages}} pages · {{count}} books finished',
      starterEyebrow: 'Your reading month',
      starterTitle: 'Start with one page',
      starterBody: 'Add a book, then log a few pages. Your month will take shape here.',
      starterAction: 'Find a book',
      summary: '{{month}}: {{pages}} pages, {{books}} books finished. Opens your month in reading.',
    },
    monthScreen: {
      title: 'Your {{month}}',
      pagesRead_one: 'page read',
      pagesRead_few: 'pages read',
      pagesRead_many: 'pages read',
      pagesRead_other: 'pages read',
      days_one: 'day with reading',
      days_few: 'days with reading',
      days_many: 'days with reading',
      days_other: 'days with reading',
      quotes_one: 'quote saved',
      quotes_few: 'quotes saved',
      quotes_many: 'quotes saved',
      quotes_other: 'quotes saved',
      goalMet: 'Goal met {{met}} of {{total}} weeks',
      goalTarget_one: '{{count}} page a day',
      goalTarget_few: '{{count}} pages a day',
      goalTarget_many: '{{count}} pages a day',
      goalTarget_other: '{{count}} pages a day',
      weeksLabel: 'Weeks on goal: {{met}} of {{total}}',
      finished: 'Finished',
      finishedOn: '{{author}} · finished {{date}}',
      rating: 'Your rating: {{count}} of 5',
      keptLine: 'A line you kept',
      keptLineSource: '{{title}} · p. {{page}} · saved {{date}}',
      keptLineSourceNoPage: '{{title}} · saved {{date}}',
      busiestDay: 'Busiest day: {{date}}, {{count}} pages',
      calendar: 'Your reading days',
      calendarDay: 'Day {{day}}: {{pages}} pages read',
      share: 'Share as an image',
      shareEyebrow: 'My {{month}} in reading',
      shareEyebrow_neuter: 'My {{month}} in reading',
      shareShelf: 'Books this month',
      shareBookPagesLegend: 'pages by book',
      shareBookmark: 'Bookmark: still reading in {{month}}',
      shareContinuationMonths: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],
      shareLongest: 'Most pages: {{title}}, {{pages}}',
      shareMore_one: 'and {{count}} more book',
      shareMore_few: 'and {{count}} more books',
      shareMore_many: 'and {{count}} more books',
      shareMore_other: 'and {{count}} more books',
      shareDay_one: 'Day {{day}}: {{count}} page',
      shareDay_few: 'Day {{day}}: {{count}} pages',
      shareDay_many: 'Day {{day}}: {{count}} pages',
      shareDay_other: 'Day {{day}}: {{count}} pages',
      shareDays_one: '{{count}} day',
      shareDays_few: '{{count}} days',
      shareDays_many: '{{count}} days',
      shareDays_other: '{{count}} days',
      shareQuotes_one: '{{count}} quote',
      shareQuotes_few: '{{count}} quotes',
      shareQuotes_many: '{{count}} quotes',
      shareQuotes_other: '{{count}} quotes',
      keptInBelore: 'Kept in Belore',
      shareUnavailable: 'Sharing isn’t available on this device.',
      shareFailed: 'The image couldn’t be shared. Try again.',
      shareNeedsBuild: 'Sharing needs a new development build.',
      error: 'Your month couldn’t load.',
      loading: 'Loading your month…',
    },
    picks: {
      eyebrow: 'From the Belore team · {{month}}',
      title: 'Belore picks',
      shelfTitle: 'Belore picks · {{month}}',
      all: 'All {{count}}',
      curator: '· a Belore team selection',
      team: 'Chosen by the Belore team',
    },
    quiz: {
      eyebrow: 'Quiz · until {{date}}',
      host: 'from the Belore team',
      asks: 'asks',
      start_one: 'Start the quiz · {{count}} question',
      start_few: 'Start the quiz · {{count}} questions',
      start_many: 'Start the quiz · {{count}} questions',
      start_other: 'Start the quiz · {{count}} questions',
      answers_one: '{{formattedCount}} reader answered',
      answers_few: '{{formattedCount}} readers answered',
      answers_many: '{{formattedCount}} readers answered',
      answers_other: '{{formattedCount}} readers answered',
      firstAnswers:
        'Be one of the first to answer. You’ll see how readers chose once more have joined in.',
      tapToAnswer: 'Tap a book to answer · question 1 of {{count}}',
      pickedInline: 'You picked {{title}} · how {{formattedCount}} readers answered',
      nextInline: 'Next question · {{current}} of {{total}}',
      answersUntil: '{{formattedCount}} readers answered · quiz ends {{date}}',
      fullResultsInline: 'Full results after your last answer · quiz ends {{date}}',
      seeResults: 'See how readers chose',
      editAnswers: 'Change my answers',
      privacy:
        'Your answers are pooled across languages and may later help personalize book recommendations.',
      answered: 'Review my answers',
      voteHint: 'Saves your choice for this question',
      seeAllQuestions: 'See all questions',
      previousEyebrow: 'Last quiz',
      readersChose: 'Readers chose',
      matched: 'You matched',
      different: 'Your pick differed',
      tie: 'Tied choice',
      allResults: 'All results',
      tiedWith: 'Also tied: {{titles}}',
      step: 'Quiz · {{current}} of {{total}}',
      pickHint: 'Pick a book. You’ll see how other readers chose after the last question.',
      next: 'Next question',
      finish: 'See results',
      back: 'Back',
      close: 'Close',
      openBook: 'Open {{title}}',
      option: '{{title}} by {{author}}',
      resultsEyebrow: 'Quiz · results',
      resultsTitle_one: 'How {{formattedCount}} reader chose',
      resultsTitle_few: 'How {{formattedCount}} readers chose',
      resultsTitle_many: 'How {{formattedCount}} readers chose',
      resultsTitle_other: 'How {{formattedCount}} readers chose',
      resultsHint: 'Your picks are marked. Tap a book to open it.',
      you: 'You',
      result: '{{title}}, {{percent}} percent',
      resultMine: '{{title}}, {{percent}} percent, your pick',
      resultsSoon: 'Results soon',
      resultsSoonBody:
        'We’ll show how readers chose once {{count}} have answered. Your answers are saved.',
      yourPicks: 'Your picks',
      addPicks: 'Add my picks to TBR',
      picksAdded: 'On your TBR',
      done: 'Done',
      submitError: 'Your answers couldn’t be sent. Try again.',
      loadError: 'The quiz couldn’t load.',
      loading: 'Loading the quiz…',
    },
    tbr: {
      eyebrow: 'Readers on Belore · this week',
      title: 'Most added to TBR',
      recentTitle: 'Readers are adding',
      top: 'Top {{count}}',
      added_one: '{{formattedCount}} reader added it',
      added_few: '{{formattedCount}} readers added it',
      added_many: '{{formattedCount}} readers added it',
      added_other: '{{formattedCount}} readers added it',
      add: 'Add {{title}} to TBR',
      onTbr: '{{title}} is on your TBR',
      read: 'You’ve read {{title}}',
    },
    finished: {
      eyebrow: 'Readers on Belore · this week',
      title: 'Just finished',
      recentTitle: 'Readers are finishing',
      count_one: '{{formattedCount}} finished',
      count_few: '{{formattedCount}} finished',
      count_many: '{{formattedCount}} finished',
      count_other: '{{formattedCount}} finished',
    },
    weekly: {
      position: 'Item {{position}}',
      eyebrow: 'Readers on Belore · this week',
      title: 'This week on Belore',
      added: 'Added',
      finished: 'Finished',
      top: 'Top 10',
      all: 'See all',
      loading: 'Loading this week’s books…',
    },
    lines: {
      eyebrow: 'Saved by readers · this week',
      title: 'Lines readers kept',
      hint: 'Quotes past your page stay hidden.',
      explore: 'Explore quotes',
      exploreHint: 'Quotes and thoughts from readers. Quotes past your page stay hidden.',
      close: 'Close quotes',
      more: 'Load more quotes',
      previous: 'Previous',
      next: 'Next',
      position: 'Quote {{number}}',
      loading: 'Loading the next quote…',
      end: 'You’ve reached the end for now. More quotes will appear as readers share them.',
      loadError: 'The next quote couldn’t load. Please try again.',
      unavailable: 'This quote couldn’t be displayed. Retry or move to the next one.',
    },
    reviews: {
      eyebrow: 'From readers’ reviews',
      title: 'Readers say',
      stars: '{{count}} of 5 stars',
      byline: '{{name}} · finished {{date}}',
      reader: 'A reader',
      readFull: 'Read full review',
      open: 'Read {{name}}’s review of {{title}}',
      review: 'Reader review',
      close: 'Close review',
      openBook: 'View book',
      folded: 'Review · {{name}} · {{rating}}',
      next: 'Next review on the Board',
      addTbr: 'To TBR',
      onTbr: 'On TBR',
      finished: 'Finished {{date}}',
      loading: 'Loading review…',
      retry: 'Try again',
      unavailable: 'This review couldn’t be loaded. It may no longer be public. You can try again.',
    },
    team: {
      eyebrow: 'From our own shelves',
      title: 'What the team is reading',
      tag: 'Belore team',
      pages: 'p. {{page}} of {{total}}',
      percent: '{{percent}}%',
      thought: 'Thought · p. {{page}}',
      thoughtNoPage: 'Thought',
      today: 'today',
      yesterday: 'yesterday',
      daysAgo_one: '{{count}} day ago',
      daysAgo_few: '{{count}} days ago',
      daysAgo_many: '{{count}} days ago',
      daysAgo_other: '{{count}} days ago',
    },
    end: {
      title: 'You’re all caught up',
      body: 'New picks every month. The next quiz starts on {{date}}.',
      bodyNoQuiz: 'New picks every month.',
    },
  },
  sk: {
    title: 'Nástenka',
    loading: 'Načítava sa nástenka…',
    error: 'Nástenku sa nepodarilo načítať.',
    retry: 'Skúsiť znova',
    loadError: {
      title: 'Nástenku sa nepodarilo načítať',
      body: 'Trochu atramentu sa nám priplietlo do cesty. Skús to o chvíľu znova.',
      refresh: 'Nástenku sa nepodarilo obnoviť. Posledný obsah tu zostáva.',
    },
    preview: {
      trigger: 'Skúsiť chybu',
      restore: 'Obnoviť nástenku',
      restoreShort: 'Obnoviť',
      menu: 'Možnosti náhľadu chyby',
      failure: 'Zobraziť chybu · opakovanie zlyhá',
      success: 'Zobraziť chybu · opakovanie uspeje',
      quotes: 'Náhľad karuselu citátov',
    },
    month: {
      eyebrow: 'Tvoj {{month}}',
      pages_one: 'strana',
      pages_few: 'strany',
      pages_many: 'strán',
      pages_other: 'strán',
      finished_one: 'dočítaná kniha',
      finished_few: 'dočítané knihy',
      finished_many: 'dočítaných kníh',
      finished_other: 'dočítaných kníh',
      weeksOnGoal: 'týždne v cieli',
      open: 'Celý mesiac',
      share: 'Zdieľať tvoj mesiac',
      compact_one: '{{pages}} strán · {{count}} dočítaná kniha',
      compact_few: '{{pages}} strán · {{count}} dočítané knihy',
      compact_many: '{{pages}} strán · {{count}} dočítaných kníh',
      compact_other: '{{pages}} strán · {{count}} dočítaných kníh',
      starterEyebrow: 'Tvoj mesiac v čítaní',
      starterTitle: 'Začni jednou stranou',
      starterBody:
        'Pridaj knihu a zaznamenaj pár prečítaných strán. Tvoj mesiac sa tu začne skladať.',
      starterAction: 'Nájsť knihu',
      summary:
        '{{month}}: {{pages}} strán, dočítané knihy: {{books}}. Otvorí tvoj mesiac v čítaní.',
    },
    monthScreen: {
      title: 'Tvoj {{month}}',
      pagesRead_one: 'prečítaná strana',
      pagesRead_few: 'prečítané strany',
      pagesRead_many: 'prečítaných strán',
      pagesRead_other: 'prečítaných strán',
      days_one: 'deň s čítaním',
      days_few: 'dni s čítaním',
      days_many: 'dní s čítaním',
      days_other: 'dní s čítaním',
      quotes_one: 'uložený citát',
      quotes_few: 'uložené citáty',
      quotes_many: 'uložených citátov',
      quotes_other: 'uložených citátov',
      goalMet: 'Cieľ splnený {{met}} z {{total}} týždňov',
      goalTarget_one: '{{count}} strana denne',
      goalTarget_few: '{{count}} strany denne',
      goalTarget_many: '{{count}} strán denne',
      goalTarget_other: '{{count}} strán denne',
      weeksLabel: 'Týždne v cieli: {{met}} z {{total}}',
      finished: 'Dočítané',
      finishedOn: '{{author}} · dočítané {{date}}',
      rating: 'Tvoje hodnotenie: {{count}} z 5',
      keptLine: 'Riadok, ktorý si si nechal',
      keptLineSource: '{{title}} · s. {{page}} · uložené {{date}}',
      keptLineSourceNoPage: '{{title}} · uložené {{date}}',
      busiestDay: 'Najčítanejší deň: {{date}}, {{count}} strán',
      calendar: 'Tvoje dni s čítaním',
      calendarDay: '{{day}}. deň: {{pages}} prečítaných strán',
      share: 'Zdieľať ako obrázok',
      shareEyebrow: 'Môj {{month}} v čítaní',
      shareEyebrow_neuter: 'Môj {{month}} v čítaní',
      shareShelf: 'Knihy mesiaca',
      shareBookPagesLegend: 'strany podľa knihy',
      shareBookmark: 'Záložka: rozčítaná, pokračuje v {{month}}',
      shareContinuationMonths: [
        'januári',
        'februári',
        'marci',
        'apríli',
        'máji',
        'júni',
        'júli',
        'auguste',
        'septembri',
        'októbri',
        'novembri',
        'decembri',
      ],
      shareLongest: 'Najviac strán: {{title}}, {{pages}}',
      shareMore_one: 'a ešte {{count}} kniha',
      shareMore_few: 'a ešte {{count}} knihy',
      shareMore_many: 'a ešte {{count}} kníh',
      shareMore_other: 'a ešte {{count}} kníh',
      shareDay_one: '{{day}}. deň: {{count}} strana',
      shareDay_few: '{{day}}. deň: {{count}} strany',
      shareDay_many: '{{day}}. deň: {{count}} strán',
      shareDay_other: '{{day}}. deň: {{count}} strán',
      shareDays_one: '{{count}} deň',
      shareDays_few: '{{count}} dni',
      shareDays_many: '{{count}} dní',
      shareDays_other: '{{count}} dní',
      shareQuotes_one: '{{count}} citát',
      shareQuotes_few: '{{count}} citáty',
      shareQuotes_many: '{{count}} citátov',
      shareQuotes_other: '{{count}} citátov',
      keptInBelore: 'Uchované v Belore',
      shareUnavailable: 'Zdieľanie nie je na tomto zariadení dostupné.',
      shareFailed: 'Obrázok sa nepodarilo zdieľať. Skús to znova.',
      shareNeedsBuild: 'Zdieľanie potrebuje nový vývojový build.',
      error: 'Tvoj mesiac sa nepodarilo načítať.',
      loading: 'Načítava sa tvoj mesiac…',
    },
    picks: {
      eyebrow: 'Od tímu Belore · {{month}}',
      title: 'Belore odporúča',
      shelfTitle: 'Belore odporúča · {{month}}',
      all: 'Všetkých {{count}}',
      curator: '· výber tímu Belore',
      team: 'Vybral tím Belore',
    },
    quiz: {
      eyebrow: 'Kvíz · do {{date}}',
      host: 'z tímu Belore',
      asks: 'sa pýta',
      start_one: 'Spustiť kvíz · {{count}} otázka',
      start_few: 'Spustiť kvíz · {{count}} otázky',
      start_many: 'Spustiť kvíz · {{count}} otázok',
      start_other: 'Spustiť kvíz · {{count}} otázok',
      answers_one: 'Odpovedal {{formattedCount}} čitateľ',
      answers_few: 'Odpovedali {{formattedCount}} čitatelia',
      answers_many: 'Odpovedalo {{formattedCount}} čitateľov',
      answers_other: 'Odpovedalo {{formattedCount}} čitateľov',
      firstAnswers:
        'Odpovedz medzi prvými. Ako volili ostatní, uvidíš, keď sa pridá viac čitateľov.',
      tapToAnswer: 'Ťukni na knihu a odpovedz · otázka 1 z {{count}}',
      pickedInline: 'Vybral si {{title}} · ako odpovedalo {{formattedCount}} čitateľov',
      nextInline: 'Ďalšia otázka · {{current}} z {{total}}',
      answersUntil: 'Odpovedalo {{formattedCount}} čitateľov · kvíz končí {{date}}',
      fullResultsInline: 'Všetky výsledky po poslednej odpovedi · kvíz končí {{date}}',
      seeResults: 'Pozrieť, ako čitatelia volili',
      editAnswers: 'Zmeniť moje odpovede',
      privacy:
        'Odpovede sa vyhodnocujú spoločne vo všetkých jazykoch a neskôr môžu pomôcť prispôsobiť odporúčania kníh.',
      answered: 'Pozrieť moje odpovede',
      voteHint: 'Uloží tvoju voľbu pri tejto otázke',
      seeAllQuestions: 'Pozrieť všetky otázky',
      previousEyebrow: 'Predchádzajúci kvíz',
      readersChose: 'Čitatelia si vybrali',
      matched: 'Zhodli ste sa',
      different: 'Tvoja voľba sa líšila',
      tie: 'Rovnaký počet hlasov',
      allResults: 'Všetky výsledky',
      tiedWith: 'Rovnako hlasov: {{titles}}',
      step: 'Kvíz · {{current}} z {{total}}',
      pickHint: 'Vyber knihu. Ako volili ostatní, uvidíš po poslednej otázke.',
      next: 'Ďalšia otázka',
      finish: 'Zobraziť výsledky',
      back: 'Späť',
      close: 'Zavrieť',
      openBook: 'Otvoriť {{title}}',
      option: '{{title}}, {{author}}',
      resultsEyebrow: 'Kvíz · výsledky',
      resultsTitle_one: 'Ako volil {{formattedCount}} čitateľ',
      resultsTitle_few: 'Ako volili {{formattedCount}} čitatelia',
      resultsTitle_many: 'Ako volilo {{formattedCount}} čitateľov',
      resultsTitle_other: 'Ako volilo {{formattedCount}} čitateľov',
      resultsHint: 'Tvoje voľby sú označené. Ťukni na knihu a otvor ju.',
      you: 'Ty',
      result: '{{title}}, {{percent}} percent',
      resultMine: '{{title}}, {{percent}} percent, tvoja voľba',
      resultsSoon: 'Výsledky čoskoro',
      resultsSoonBody:
        'Ako čitatelia volili, ukážeme, keď ich odpovie aspoň {{count}}. Tvoje odpovede sú uložené.',
      yourPicks: 'Tvoje voľby',
      addPicks: 'Pridať moje voľby do TBR',
      picksAdded: 'V tvojom TBR',
      done: 'Hotovo',
      submitError: 'Odpovede sa nepodarilo odoslať. Skús to znova.',
      loadError: 'Kvíz sa nepodarilo načítať.',
      loading: 'Načítava sa kvíz…',
    },
    tbr: {
      eyebrow: 'Čitatelia Belore · tento týždeň',
      title: 'Najčastejšie pridávané do TBR',
      recentTitle: 'Čitatelia si pridávajú',
      top: 'Top {{count}}',
      added_one: 'Pridal {{formattedCount}} čitateľ',
      added_few: 'Pridali {{formattedCount}} čitatelia',
      added_many: 'Pridalo {{formattedCount}} čitateľov',
      added_other: 'Pridalo {{formattedCount}} čitateľov',
      add: 'Pridať {{title}} do TBR',
      onTbr: '{{title}} je v tvojom TBR',
      read: '{{title}} si už prečítal',
    },
    finished: {
      eyebrow: 'Čitatelia Belore · tento týždeň',
      title: 'Práve dočítané',
      recentTitle: 'Čitatelia dočítavajú',
      count_one: 'Dočítal {{formattedCount}}',
      count_few: 'Dočítali {{formattedCount}}',
      count_many: 'Dočítalo {{formattedCount}}',
      count_other: 'Dočítalo {{formattedCount}}',
    },
    weekly: {
      position: 'Položka {{position}}',
      eyebrow: 'Čitatelia Belore · tento týždeň',
      title: 'Tento týždeň na Belore',
      added: 'Pridané',
      finished: 'Dočítané',
      top: 'Top 10',
      all: 'Zobraziť všetky',
      loading: 'Načítavajú sa knihy tohto týždňa…',
    },
    lines: {
      eyebrow: 'Uložené čitateľmi · tento týždeň',
      title: 'Riadky, ktoré si čitatelia nechali',
      hint: 'Citáty za tvojou stranou zostávajú skryté.',
      explore: 'Objavovať citáty',
      exploreHint: 'Citáty a myšlienky čitateľov. Citáty za tvojou stranou zostávajú skryté.',
      close: 'Zavrieť citáty',
      more: 'Načítať ďalšie citáty',
      previous: 'Späť',
      next: 'Ďalší',
      position: 'Citát {{number}}',
      loading: 'Načítavam ďalší citát…',
      end: 'Zatiaľ si na konci. Ďalšie citáty pribudnú, keď ich čitatelia zdieľajú.',
      loadError: 'Ďalší citát sa nepodarilo načítať. Skús to znova.',
      unavailable: 'Tento citát sa nepodarilo zobraziť. Skús to znova alebo prejdi na ďalší.',
    },
    reviews: {
      eyebrow: 'Z recenzií čitateľov',
      title: 'Čitatelia píšu',
      stars: '{{count}} z 5 hviezdičiek',
      byline: '{{name}} · dočítané {{date}}',
      reader: 'Čitateľ',
      readFull: 'Prečítať celú recenziu',
      open: 'Prečítať recenziu od {{name}} na knihu {{title}}',
      review: 'Čitateľská recenzia',
      close: 'Zavrieť recenziu',
      openBook: 'Zobraziť knihu',
      folded: 'Recenzia · {{name}} · {{rating}}',
      next: 'Ďalšia recenzia na Nástenke',
      addTbr: 'Do TBR',
      onTbr: 'V TBR',
      finished: 'Dočítané {{date}}',
      loading: 'Načítavam recenziu…',
      retry: 'Skúsiť znova',
      unavailable:
        'Recenziu sa nepodarilo načítať. Možno už nie je verejná. Môžeš to skúsiť znova.',
    },
    team: {
      eyebrow: 'Z našich políc',
      title: 'Čo práve čítame',
      tag: 'Tím Belore',
      pages: 's. {{page}} z {{total}}',
      percent: '{{percent}} %',
      thought: 'Myšlienka · s. {{page}}',
      thoughtNoPage: 'Myšlienka',
      today: 'dnes',
      yesterday: 'včera',
      daysAgo_one: 'pred {{count}} dňom',
      daysAgo_few: 'pred {{count}} dňami',
      daysAgo_many: 'pred {{count}} dňami',
      daysAgo_other: 'pred {{count}} dňami',
    },
    end: {
      title: 'Všetko máš prečítané',
      body: 'Nové odporúčania každý mesiac. Ďalší kvíz začína {{date}}.',
      bodyNoQuiz: 'Nové odporúčania každý mesiac.',
    },
  },
  cs: {
    title: 'Nástěnka',
    loading: 'Načítá se nástěnka…',
    error: 'Nástěnku se nepodařilo načíst.',
    retry: 'Zkusit znovu',
    loadError: {
      title: 'Nástěnku se nepodařilo načíst',
      body: 'Trochu inkoustu se nám připletlo do cesty. Zkus to za chvíli znovu.',
      refresh: 'Nástěnku se nepodařilo obnovit. Poslední obsah tu zůstává.',
    },
    preview: {
      trigger: 'Zkusit chybu',
      restore: 'Obnovit nástěnku',
      restoreShort: 'Obnovit',
      menu: 'Možnosti náhledu chyby',
      failure: 'Zobrazit chybu · opakování selže',
      success: 'Zobrazit chybu · opakování uspěje',
      quotes: 'Náhled karuselu citátů',
    },
    month: {
      eyebrow: 'Tvůj {{month}}',
      pages_one: 'strana',
      pages_few: 'strany',
      pages_many: 'stran',
      pages_other: 'stran',
      finished_one: 'dočtená kniha',
      finished_few: 'dočtené knihy',
      finished_many: 'dočtených knih',
      finished_other: 'dočtených knih',
      weeksOnGoal: 'týdny v cíli',
      open: 'Celý měsíc',
      share: 'Sdílet tvůj měsíc',
      compact_one: '{{pages}} stran · {{count}} dočtená kniha',
      compact_few: '{{pages}} stran · {{count}} dočtené knihy',
      compact_many: '{{pages}} stran · {{count}} dočtených knih',
      compact_other: '{{pages}} stran · {{count}} dočtených knih',
      starterEyebrow: 'Tvůj měsíc ve čtení',
      starterTitle: 'Začni jednou stranou',
      starterBody:
        'Přidej knihu a zaznamenej pár přečtených stran. Tvůj měsíc se tu začne skládat.',
      starterAction: 'Najít knihu',
      summary: '{{month}}: {{pages}} stran, dočtené knihy: {{books}}. Otevře tvůj měsíc ve čtení.',
    },
    monthScreen: {
      title: 'Tvůj {{month}}',
      pagesRead_one: 'přečtená strana',
      pagesRead_few: 'přečtené strany',
      pagesRead_many: 'přečtených stran',
      pagesRead_other: 'přečtených stran',
      days_one: 'den se čtením',
      days_few: 'dny se čtením',
      days_many: 'dní se čtením',
      days_other: 'dní se čtením',
      quotes_one: 'uložený citát',
      quotes_few: 'uložené citáty',
      quotes_many: 'uložených citátů',
      quotes_other: 'uložených citátů',
      goalMet: 'Cíl splněn {{met}} z {{total}} týdnů',
      goalTarget_one: '{{count}} strana denně',
      goalTarget_few: '{{count}} strany denně',
      goalTarget_many: '{{count}} stran denně',
      goalTarget_other: '{{count}} stran denně',
      weeksLabel: 'Týdny v cíli: {{met}} z {{total}}',
      finished: 'Dočteno',
      finishedOn: '{{author}} · dočteno {{date}}',
      rating: 'Tvoje hodnocení: {{count}} z 5',
      keptLine: 'Řádek, který sis nechal',
      keptLineSource: '{{title}} · s. {{page}} · uloženo {{date}}',
      keptLineSourceNoPage: '{{title}} · uloženo {{date}}',
      busiestDay: 'Nejčtenější den: {{date}}, {{count}} stran',
      calendar: 'Tvoje dny se čtením',
      calendarDay: '{{day}}. den: {{pages}} přečtených stran',
      share: 'Sdílet jako obrázek',
      shareEyebrow: 'Můj {{month}} ve čtení',
      shareEyebrow_neuter: 'Mé {{month}} ve čtení',
      shareShelf: 'Knihy měsíce',
      shareBookPagesLegend: 'strany podle knihy',
      shareBookmark: 'Záložka: rozečtená, pokračuje v {{month}}',
      shareContinuationMonths: [
        'lednu',
        'únoru',
        'březnu',
        'dubnu',
        'květnu',
        'červnu',
        'červenci',
        'srpnu',
        'září',
        'říjnu',
        'listopadu',
        'prosinci',
      ],
      shareLongest: 'Nejvíce stran: {{title}}, {{pages}}',
      shareMore_one: 'a ještě {{count}} kniha',
      shareMore_few: 'a ještě {{count}} knihy',
      shareMore_many: 'a ještě {{count}} knih',
      shareMore_other: 'a ještě {{count}} knih',
      shareDay_one: '{{day}}. den: {{count}} strana',
      shareDay_few: '{{day}}. den: {{count}} strany',
      shareDay_many: '{{day}}. den: {{count}} stran',
      shareDay_other: '{{day}}. den: {{count}} stran',
      shareDays_one: '{{count}} den',
      shareDays_few: '{{count}} dny',
      shareDays_many: '{{count}} dní',
      shareDays_other: '{{count}} dní',
      shareQuotes_one: '{{count}} citát',
      shareQuotes_few: '{{count}} citáty',
      shareQuotes_many: '{{count}} citátů',
      shareQuotes_other: '{{count}} citátů',
      keptInBelore: 'Uchováno v Belore',
      shareUnavailable: 'Sdílení není na tomto zařízení dostupné.',
      shareFailed: 'Obrázek se nepodařilo sdílet. Zkus to znovu.',
      shareNeedsBuild: 'Sdílení potřebuje nový vývojový build.',
      error: 'Tvůj měsíc se nepodařilo načíst.',
      loading: 'Načítá se tvůj měsíc…',
    },
    picks: {
      eyebrow: 'Od týmu Belore · {{month}}',
      title: 'Belore doporučuje',
      shelfTitle: 'Belore doporučuje · {{month}}',
      all: 'Všech {{count}}',
      curator: '· výběr týmu Belore',
      team: 'Vybral tým Belore',
    },
    quiz: {
      eyebrow: 'Kvíz · do {{date}}',
      host: 'z týmu Belore',
      asks: 'se ptá',
      start_one: 'Spustit kvíz · {{count}} otázka',
      start_few: 'Spustit kvíz · {{count}} otázky',
      start_many: 'Spustit kvíz · {{count}} otázek',
      start_other: 'Spustit kvíz · {{count}} otázek',
      answers_one: 'Odpověděl {{formattedCount}} čtenář',
      answers_few: 'Odpověděli {{formattedCount}} čtenáři',
      answers_many: 'Odpovědělo {{formattedCount}} čtenářů',
      answers_other: 'Odpovědělo {{formattedCount}} čtenářů',
      firstAnswers: 'Odpověz mezi prvními. Jak volili ostatní, uvidíš, až se přidá víc čtenářů.',
      tapToAnswer: 'Klepni na knihu a odpověz · otázka 1 z {{count}}',
      pickedInline: 'Vybral sis {{title}} · jak odpovědělo {{formattedCount}} čtenářů',
      nextInline: 'Další otázka · {{current}} z {{total}}',
      answersUntil: 'Odpovědělo {{formattedCount}} čtenářů · kvíz končí {{date}}',
      fullResultsInline: 'Všechny výsledky po poslední odpovědi · kvíz končí {{date}}',
      seeResults: 'Podívat se, jak čtenáři volili',
      editAnswers: 'Změnit moje odpovědi',
      privacy:
        'Odpovědi se vyhodnocují společně ve všech jazycích a později mohou pomoci přizpůsobit doporučení knih.',
      answered: 'Zobrazit mé odpovědi',
      voteHint: 'Uloží tvou volbu u této otázky',
      seeAllQuestions: 'Zobrazit všechny otázky',
      previousEyebrow: 'Předchozí kvíz',
      readersChose: 'Čtenáři si vybrali',
      matched: 'Shodli jste se',
      different: 'Tvá volba se lišila',
      tie: 'Stejný počet hlasů',
      allResults: 'Všechny výsledky',
      tiedWith: 'Stejně hlasů: {{titles}}',
      step: 'Kvíz · {{current}} z {{total}}',
      pickHint: 'Vyber knihu. Jak volili ostatní, uvidíš po poslední otázce.',
      next: 'Další otázka',
      finish: 'Zobrazit výsledky',
      back: 'Zpět',
      close: 'Zavřít',
      openBook: 'Otevřít {{title}}',
      option: '{{title}}, {{author}}',
      resultsEyebrow: 'Kvíz · výsledky',
      resultsTitle_one: 'Jak volil {{formattedCount}} čtenář',
      resultsTitle_few: 'Jak volili {{formattedCount}} čtenáři',
      resultsTitle_many: 'Jak volilo {{formattedCount}} čtenářů',
      resultsTitle_other: 'Jak volilo {{formattedCount}} čtenářů',
      resultsHint: 'Tvoje volby jsou označené. Klepni na knihu a otevři ji.',
      you: 'Ty',
      result: '{{title}}, {{percent}} procent',
      resultMine: '{{title}}, {{percent}} procent, tvoje volba',
      resultsSoon: 'Výsledky brzy',
      resultsSoonBody:
        'Jak čtenáři volili, ukážeme, až jich odpoví aspoň {{count}}. Tvoje odpovědi jsou uložené.',
      yourPicks: 'Tvoje volby',
      addPicks: 'Přidat moje volby do TBR',
      picksAdded: 'V tvém TBR',
      done: 'Hotovo',
      submitError: 'Odpovědi se nepodařilo odeslat. Zkus to znovu.',
      loadError: 'Kvíz se nepodařilo načíst.',
      loading: 'Načítá se kvíz…',
    },
    tbr: {
      eyebrow: 'Čtenáři Belore · tento týden',
      title: 'Nejčastěji přidávané do TBR',
      recentTitle: 'Čtenáři si přidávají',
      top: 'Top {{count}}',
      added_one: 'Přidal {{formattedCount}} čtenář',
      added_few: 'Přidali {{formattedCount}} čtenáři',
      added_many: 'Přidalo {{formattedCount}} čtenářů',
      added_other: 'Přidalo {{formattedCount}} čtenářů',
      add: 'Přidat {{title}} do TBR',
      onTbr: '{{title}} je v tvém TBR',
      read: '{{title}} už máš přečtenou',
    },
    finished: {
      eyebrow: 'Čtenáři Belore · tento týden',
      title: 'Právě dočteno',
      recentTitle: 'Čtenáři dočítají',
      count_one: 'Dočetl {{formattedCount}}',
      count_few: 'Dočetli {{formattedCount}}',
      count_many: 'Dočetlo {{formattedCount}}',
      count_other: 'Dočetlo {{formattedCount}}',
    },
    weekly: {
      position: 'Položka {{position}}',
      eyebrow: 'Čtenáři Belore · tento týden',
      title: 'Tento týden na Belore',
      added: 'Přidané',
      finished: 'Dočtené',
      top: 'Top 10',
      all: 'Zobrazit všechny',
      loading: 'Načítají se knihy tohoto týdne…',
    },
    lines: {
      eyebrow: 'Uloženo čtenáři · tento týden',
      title: 'Řádky, které si čtenáři nechali',
      hint: 'Citáty za tvou stranou zůstávají skryté.',
      explore: 'Objevovat citáty',
      exploreHint: 'Citáty a myšlenky čtenářů. Citáty za tvou stranou zůstávají skryté.',
      close: 'Zavřít citáty',
      more: 'Načíst další citáty',
      previous: 'Zpět',
      next: 'Další',
      position: 'Citát {{number}}',
      loading: 'Načítám další citát…',
      end: 'Zatím jsi na konci. Další citáty přibudou, až je čtenáři sdílejí.',
      loadError: 'Další citát se nepodařilo načíst. Zkus to znovu.',
      unavailable: 'Tento citát se nepodařilo zobrazit. Zkus to znovu nebo přejdi na další.',
    },
    reviews: {
      eyebrow: 'Z recenzí čtenářů',
      title: 'Čtenáři píšou',
      stars: '{{count}} z 5 hvězdiček',
      byline: '{{name}} · dočteno {{date}}',
      reader: 'Čtenář',
      readFull: 'Přečíst celou recenzi',
      open: 'Přečíst recenzi od {{name}} na knihu {{title}}',
      review: 'Čtenářská recenze',
      close: 'Zavřít recenzi',
      openBook: 'Zobrazit knihu',
      folded: 'Recenze · {{name}} · {{rating}}',
      next: 'Další recenze na Nástěnce',
      addTbr: 'Do TBR',
      onTbr: 'V TBR',
      finished: 'Dočteno {{date}}',
      loading: 'Načítám recenzi…',
      retry: 'Zkusit znovu',
      unavailable: 'Recenzi se nepodařilo načíst. Možná už není veřejná. Můžeš to zkusit znovu.',
    },
    team: {
      eyebrow: 'Z našich polic',
      title: 'Co právě čteme',
      tag: 'Tým Belore',
      pages: 's. {{page}} z {{total}}',
      percent: '{{percent}} %',
      thought: 'Myšlenka · s. {{page}}',
      thoughtNoPage: 'Myšlenka',
      today: 'dnes',
      yesterday: 'včera',
      daysAgo_one: 'před {{count}} dnem',
      daysAgo_few: 'před {{count}} dny',
      daysAgo_many: 'před {{count}} dny',
      daysAgo_other: 'před {{count}} dny',
    },
    end: {
      title: 'Máš všechno přečtené',
      body: 'Nová doporučení každý měsíc. Další kvíz začíná {{date}}.',
      bodyNoQuiz: 'Nová doporučení každý měsíc.',
    },
  },
} as const;
